import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectRider } from '../features/selectedRiderSlice';
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button';

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { db } from '../firebase'
import { collection, orderBy, query, onSnapshot } from 'firebase/firestore';
import { useUtils } from '../utils/utils';
import firebaseService from '../services/FirebaseService';
import RideInfoLine from "./RideInfoLine";

function RideMarkers({rideDate}) {
  const { getFormattedDateTime, formatTextDate } = useUtils();
  const rider = useSelector(selectRider);
  const [locs, setLocs] = useState([]);
  const [status, setStatus] = useState(undefined);
  const [rideInfo, setRideInfo] = useState(undefined);
  const [haveAllRideInfo, setHaveAllRideInfo] = useState(false);
  const markersRef = useRef([]);
  const map = useMap();
  const markerLib = useMapsLibrary('marker');
  const navigate = useNavigate();

  const getStaticLocations = useCallback(async () => {
    const headers = {
      userId: rider.uid, 
      rideDate: rideDate ? rideDate : rider.latestRideId
    }
    const result = await firebaseService.callFunction('getLocationsForDay', {}, headers)
    setStatus(result.data.status)
    setRideInfo(result.data)
    //Set locations only if ride is complete
    if (result.data.status === 'complete') {
      setLocs(result.data.locations)

      //If ride summary has been done, we will have distance
      if (result.data.distance) {
        setHaveAllRideInfo(true);
      }
    }
  }, [rider, rideDate])


  useEffect(() => {
    if (!rider) {
      return;
    }
    //This will get locations and if ride is complete set the locations
    //if ride is ongoing, we will subscribe to updates
    getStaticLocations()
  }, [getStaticLocations, rider, rideDate])

  //Sets up a subscription to rider's location updates for the day. 
  useEffect(() => {
    let locationsUnsubscribe;
    if (!rider) {
      return;
    }

    if (status && status === 'ongoing') {
      const locationsRef = collection(db, `/users/${rider.uid}/rides/${rider.latestRideId}/locations`)
      const q = query(locationsRef, orderBy('time'));
      
      locationsUnsubscribe = onSnapshot(q, locationsSnapshot => {
        //Process all location updates
        const newLocs = locationsSnapshot.docs.map(doc => doc.data());
  
        setLocs(newLocs);
      });
    }

    return () => {
      if (locationsUnsubscribe) {
        locationsUnsubscribe();
      } 
    };
  }, [rider, rideDate, status, getStaticLocations])

  const getRideStatus = () => {
    return locs.some(loc => loc.type === 'last') ? {status: 'Ended', color: 'success'} : {status: 'In Progress', color: 'info'};
  }

  const getRideDate = () => {
    return formatTextDate(rideDate ? rideDate : rider.latestRideId)
  }

  const getLastUpdateTime = () => {
    const dateTimeDisplay = getFormattedDateTime(locs[locs.length - 1].time);

    return dateTimeDisplay;
  }

  const Link = ({ id, children, title }) => (
    <OverlayTrigger overlay={<Tooltip id={id}>{title}</Tooltip>}>
      <span><u>{children}</u></span>
    </OverlayTrigger>
  );

  //Programmatically remove old markers and add new markers
  const setMarkers = () => {
    if (!rider || !map || !markerLib) {
      return false;
    }

    const greenMarkerOptions = {background: '#82E0AA', glyphColor: '#F4D03F'};
    const redMarkerOptions = {background: 'red'};

    //remove existing markers
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];
          
    const newMarkers = []; 
    let bounds = new window.google.maps.LatLngBounds();

    const lastIndex = locs.length - 1;
    locs.forEach((loc, idx) => {
      const dateTimeDisplay = getFormattedDateTime(loc.time);
      var redMarker = true;
      if (status === 'ongoing') {
        if (idx === lastIndex) {
          redMarker = false;
        }
      } else {
        if (loc.type === 'last') {
          redMarker = false;
        }
      }

      const markerOptions = {
        map,
        position: {lat: loc.latitude, lng: loc.longitude},
        zIndex: idx === lastIndex ? 2 : 1,
        content: redMarker ? 
                          new markerLib.PinElement(redMarkerOptions).element : 
                          new markerLib.PinElement(greenMarkerOptions).element,
        title: `Altitude: ${loc.altitude?.toFixed(2)} metres, your time: ${dateTimeDisplay}, type: ${loc.type}`
      }
      newMarkers.push(new markerLib.AdvancedMarkerElement(markerOptions));
      bounds.extend(new window.google.maps.LatLng(loc.latitude, loc.longitude));
    })
          
    if (newMarkers.length > 0) {
      map.fitBounds(bounds);
    }
        
    //Set new markers
    markersRef.current = ([...newMarkers]);
    return true;
  }

  return (
    <>
      {
        //set markers using new location updates
        locs.length > 0 && setMarkers() && 
        <>
          {
            haveAllRideInfo &&
            <>
              <div className="card-title d-flex justify-content-between">
                <strong>{getRideDate()}</strong>
                <h4>{rideInfo.title}</h4>
                <Button as="a" variant='link' onClick={() => navigate('/tours', { state: { activeTab: 'dayRides' } })}>Back to Day Rides</Button>
              </div>
              <div className="d-flex align-items-center mb-2">
                {rideInfo && <RideInfoLine rideInfo={rideInfo} />}
              </div>
            </>
          }
          {
            !haveAllRideInfo && 
            <div className="card-title d-flex justify-content-between align-items-center mb-1">
              <span>Ride Date: <Badge bg='info'>{getRideDate()}</Badge></span>
              <span>Ride Status: <Badge bg={getRideStatus().color}>{getRideStatus().status}</Badge></span>
              <span>Last Update: <Badge bg='info'>{getLastUpdateTime()}</Badge></span>
              <span>Update Count: <Badge bg='info'>{locs.length}</Badge></span>
              <span><Link title='Hover over markers to see time and altitude' id='t-1'>Info</Link></span>
              <span>
                <Button as="a" variant='link' onClick={() => navigate('/tours', { state: { activeTab: 'dayRides' } })}>Back to Day Rides</Button>
              </span>
            </div>
          }
        </>
      }
    </>
  );
}

export default RideMarkers;